"use client"

import { useEffect } from "react"
import { isHeatmapPreview } from "./preview"

// Runs inside the storefront page ONLY when it's embedded in the admin heatmap
// viewer (`?pa_heatmap=1`, inside an iframe). It lets the cross-origin admin
// drive the page's scroll and learn its full height, so the admin's heatmap
// overlay can stay glued to the real page content as it scrolls.
//
// Protocol (postMessage, all messages tagged `__pa_heatmap: true`):
//   admin → page : { type: "scrollTo", y }   → window.scrollTo(0, y)
//   admin → page : { type: "measure" }        → re-send metrics
//   page  → admin : { type: "metrics", scrollHeight, viewportW, viewportH }
const TAG = "__pa_heatmap"

export default function HeatmapBridge() {
  useEffect(() => {
    // Only activate in an embedded preview, never in normal browsing.
    if (window.parent === window || !isHeatmapPreview()) return

    const postMetrics = () => {
      const scrollHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body?.scrollHeight ?? 0
      )
      window.parent.postMessage(
        {
          [TAG]: true,
          type: "metrics",
          scrollHeight,
          viewportW: window.innerWidth,
          viewportH: window.innerHeight,
        },
        "*"
      )
    }

    const onMessage = (e: MessageEvent) => {
      const d = e.data
      if (!d || d[TAG] !== true) return
      if (d.type === "scrollTo") {
        window.scrollTo(0, Number(d.y) || 0)
      } else if (d.type === "measure") {
        postMetrics()
      }
    }

    window.addEventListener("message", onMessage)
    window.addEventListener("resize", postMetrics)

    // The page height changes as fonts/images settle — keep the admin updated.
    const ro = new ResizeObserver(postMetrics)
    ro.observe(document.documentElement)
    if (document.body) ro.observe(document.body)

    postMetrics()
    const timers = [
      window.setTimeout(postMetrics, 500),
      window.setTimeout(postMetrics, 1500),
      window.setTimeout(postMetrics, 3000),
    ]

    return () => {
      window.removeEventListener("message", onMessage)
      window.removeEventListener("resize", postMetrics)
      ro.disconnect()
      timers.forEach((t) => window.clearTimeout(t))
    }
  }, [])

  return null
}
